import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { feedbackConfigurationApi } from '@/api/feedback/feedbackConfigurationApi';
import { ScheduledReportFrequency, type ReportConfiguration } from '@/models/feedback/Configuration';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import { useToast } from '../../common/toast/ToastProvider';
import ReadOnlyFieldset from '../../common/dialog/ReadOnlyFieldset';

interface Props {
    open: boolean;
    brandId: string;
    locationId?: string;
    report: ReportConfiguration | null; // null = create
    // View-only: the grid opens this with an eye rather than a pencil for read access alone.
    readOnly?: boolean;
    onClose: () => void;
}

const ReportDialog = ({ open, brandId, locationId, report, readOnly, onClose }: Props) => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();

    const [appliesTo, setAppliesTo] = useState<'brand' | 'location'>('brand');
    const [emails, setEmails] = useState('');
    const [frequency, setFrequency] = useState<ScheduledReportFrequency>(ScheduledReportFrequency.Daily);

    useEffect(() => {
        if (!open) return;
        if (report) {
            setAppliesTo(report.locationId ? 'location' : 'brand');
            setEmails(report.reportEmailAddresses ?? '');
            setFrequency(report.reportFrequency);
        } else {
            setAppliesTo(locationId ? 'location' : 'brand');
            setEmails('');
            setFrequency(ScheduledReportFrequency.Daily);
        }
    }, [open, report, locationId]);

    const { mutateAsync: save, isPending } = useMutation({
        mutationFn: async () => {
            const body = {
                brandId: appliesTo === 'brand' ? brandId : null,
                locationId: appliesTo === 'location' ? (locationId ?? null) : null,
                reportEmailAddresses: emails,
                reportFrequency: frequency,
            };
            if (report) {
                await feedbackConfigurationApi.updateReport(report.id, body);
            } else {
                await feedbackConfigurationApi.createReport(body);
            }
        },
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.feedbackReportSaved });
            await qc.invalidateQueries({ queryKey: ['feedback-reports', brandId ?? '', locationId ?? ''] });
            onClose();
        },
        onError: () => toast({ severity: 'error', message: strings.feedbackReportSaveError }),
    });

    const targetValid = appliesTo === 'brand' ? !!brandId : !!locationId;
    const canSave = emails.trim().length > 0 && targetValid;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{report ? strings.feedbackEditReport : strings.feedbackNewReport}</DialogTitle>
            <DialogContent>
                <ReadOnlyFieldset readOnly={readOnly}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <FormControl fullWidth>
                        <InputLabel id="report-applies-label">{strings.feedbackAppliesTo}</InputLabel>
                        <Select
                            labelId="report-applies-label"
                            label={strings.feedbackAppliesTo}
                            value={appliesTo}
                            onChange={(e) => setAppliesTo(e.target.value as 'brand' | 'location')}
                        >
                            <MenuItem value="brand">{strings.feedbackEntireBrand}</MenuItem>
                            <MenuItem value="location" disabled={!locationId}>{strings.feedbackSelectedLocation}</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label={strings.feedbackReportEmails}
                        value={emails}
                        onChange={(e) => setEmails(e.target.value)}
                        helperText={strings.feedbackCommaSeparatedEmails}
                        fullWidth
                        multiline
                        minRows={2}
                    />

                    <FormControl fullWidth>
                        <InputLabel id="report-frequency-label">{strings.feedbackFrequency}</InputLabel>
                        <Select
                            labelId="report-frequency-label"
                            label={strings.feedbackFrequency}
                            value={frequency}
                            onChange={(e) => setFrequency(Number(e.target.value) as ScheduledReportFrequency)}
                        >
                            <MenuItem value={ScheduledReportFrequency.None}>{strings.feedbackFrequencyNone}</MenuItem>
                            <MenuItem value={ScheduledReportFrequency.Daily}>{strings.feedbackFrequencyDaily}</MenuItem>
                            <MenuItem value={ScheduledReportFrequency.Weekly}>{strings.feedbackFrequencyWeekly}</MenuItem>
                            <MenuItem value={ScheduledReportFrequency.Monthly}>{strings.feedbackFrequencyMonthly}</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </ReadOnlyFieldset>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>{readOnly ? strings.close : strings.cancel}</Button>
                {!readOnly && <Button variant="contained" loading={isPending} disabled={!canSave} onClick={() => save()}>{strings.save}</Button>}
            </DialogActions>
        </Dialog>
    );
};

export default ReportDialog;
